import { createAsyncThunk, createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit';
import { AppState } from '.';
import {
  AwsCheckoutRequest,
  exceptionOf,
  Feature,
  isSameFeature,
  isSamePlanIgnoringEcom,
  Operator,
  Order,
  OrderCheckoutRequest,
  OrderPreviewRequest,
  OrderPreviewResponse,
  pendingOrderStatus,
  ProductPreviewRequest,
  Query,
  SerializedException,
} from '../models';
import { OrderService } from '../services';

export const ORDER_FEATURE_KEY = 'order';

export interface OrderState {
  loading: boolean;
  errors: SerializedException[];
  addOns?: Record<string, number>;
  truePlatformPreviewRequest?: ProductPreviewRequest;
  checkoutError: SerializedException;
  previousOrder?: Order[];
  pendingOrder?: Order[];
  orderResponse?: Order;
  prevOrderPreviewRequest?: ProductPreviewRequest;
  orderPreviewRequest?: OrderPreviewRequest;
  orderPreviewResponse?: OrderPreviewResponse;
  previewRequestId?: string;
  previewLoading: boolean;
  saveCardAndBillingInfoBtnEnabled: boolean;
  termsAndConditionsAccepted: boolean;
  couponName: string | null;
  couponError: string | null;
}

const orderAdapter = createEntityAdapter<OrderState>();

export const createInitialState = (partialState: Partial<OrderState> = {}) => (
  orderAdapter.getInitialState({
    ...partialState,
    loading: false,
    previewLoading: false,
    errors: [] as SerializedException[],
    saveCardAndBillingInfoBtnEnabled: true,
    termsAndConditionsAccepted: false,
    couponName: null as string | null,
    couponError: null as string | null,
  })
);

export const doFetchAllOrder = createAsyncThunk(
  'order/getAlOrder',
  async (accountId: number, { rejectWithValue }) => {
    try {
      const criteria: Query<Order>[] = [
        {
          field: 'archived',
          operator: Operator.EQ,
          value: false,
        },
        {
          field: 'accountId',
          operator: Operator.EQ,
          value: accountId,
        },
        {
          field: 'id',
          desc: true,
        },
      ];
      const data = await OrderService.fetchOrders(...criteria);
      return { data };
    } catch (e: any) {
      return rejectWithValue(exceptionOf(e).toJson());
    }
  },
);

export const doCheckout = createAsyncThunk(
  'order/checkout',
  async (
    order: OrderCheckoutRequest,
    { rejectWithValue },
  ) => {
    try {
      const data = await OrderService.checkout(order);
      return { data };
    } catch (e: any) {
      return rejectWithValue(exceptionOf(e).toJson());
    }
  },
);

export const doPreviewOrder = createAsyncThunk(
  'order/previewOrder',
  async (previewRequest: OrderPreviewRequest, { rejectWithValue, signal }) => {
    try {
      const orderPreviewResponse = await OrderService.previewOrder(previewRequest, signal);
      return { orderPreviewResponse };
    } catch (e: any) {
      if (signal.aborted) {
        throw e;
      }
      return rejectWithValue(exceptionOf(e).toJson());
    }
  },
);

export const doCancelOrder = createAsyncThunk(
  'order/cancelOrder',
  async (orderId: number, { rejectWithValue }) => {
    try {
      const orderCancelResponse = await OrderService.cancelOrder(orderId);
      return { orderCancelResponse };
    } catch (e: any) {
      return rejectWithValue(exceptionOf(e).toJson());
    }
  },
);

export const doFetchOrderById = createAsyncThunk(
  'order/fetchOrderById',
  async (orderId: number, { rejectWithValue }) => {
    try {
      const order = await OrderService.fetchOrderById(orderId);
      return { order };
    } catch (e: any) {
      return rejectWithValue(exceptionOf(e).toJson());
    }
  },
);

export const doCheckoutAws = createAsyncThunk(
  'order/checkoutAws',
  async (
    request: AwsCheckoutRequest,
    { rejectWithValue },
  ) => {
    try {
      const data = await OrderService.checkoutAws(request);
      return { data };
    } catch (e: any) {
      return rejectWithValue(exceptionOf(e).toJson());
    }
  },
);

const orderSlice = createSlice({
  name: ORDER_FEATURE_KEY,
  initialState: createInitialState(),
  reducers: {
    setAddOns: (state, action: { payload: Record<string, number> }) => {
      state.addOns = action.payload;
    },
    setPrevTruePlatformPreviewRequest: (state, action: { payload: ProductPreviewRequest }) => {
      state.prevOrderPreviewRequest = action.payload;
    },
    setTruePlatformPreviewRequest: (state, action: { payload: ProductPreviewRequest }) => {
      state.truePlatformPreviewRequest = action.payload;
    },
    updateSaveCardAndBillingInfoBtnEnabled: (state, action: { payload: boolean }) => {
      state.saveCardAndBillingInfoBtnEnabled = action.payload;
    },
    updateProductPreviewRequest: (
      state,
      action: { payload: { product: ProductPreviewRequest, keepZeroQuantity?: boolean } },
    ) => {
      const currentProducts = state.orderPreviewRequest?.singleProductList ?? [];
      const currentProductIndex = currentProducts.findIndex(
        product => isSameFeature(product.productCode, action.payload.product.productCode),
      );
      const shouldRemoveZeroQuantity = action.payload.product.quantity === 0
        && !action.payload.keepZeroQuantity;

      if (shouldRemoveZeroQuantity && currentProductIndex === -1) {
        return;
      }

      if (shouldRemoveZeroQuantity) {
        state.orderPreviewRequest = {
          singleProductList: currentProducts.filter(
            product => !isSameFeature(product.productCode, action.payload.product.productCode),
          ),
        };
        return;
      }

      if (currentProductIndex === -1) {
        state.orderPreviewRequest = {
          singleProductList: [
            ...currentProducts,
            action.payload.product,
          ],
        };
        return;
      }

      state.orderPreviewRequest = {
        singleProductList: currentProducts.map((product, index) => (
          index === currentProductIndex ? action.payload.product : product
        )),
      };
    },
    updateTermsAndConditionsAccepted: (state, action: { payload: boolean }) => {
      state.termsAndConditionsAccepted = action.payload;
    },
    resetOrderPreviewRequest: state => {
      state.addOns = undefined;
      state.truePlatformPreviewRequest = undefined;
      state.orderPreviewRequest = undefined;
      state.previewRequestId = undefined;
    },
    resetOrderPreviewResponse: state => {
      state.loading = false;
      state.previewLoading = false;
      state.previewRequestId = undefined;
      state.orderPreviewResponse = undefined;
    },
    resetOrderResponse: state => {
      state.orderResponse = undefined;
    },
    resetTermsAndConditionsAccepted: state => {
      state.termsAndConditionsAccepted = false;
    },
    setCouponName: (state, action: { payload: string }) => {
      state.couponName = action.payload;
      state.couponError = null;
    },
    clearCoupon: state => {
      state.couponName = null;
      state.couponError = null;
    },
  },
  extraReducers: builder => {
    builder.addCase(doCheckout.pending, state => {
      state.loading = true;
      state.checkoutError = undefined;
      state.orderResponse = undefined;
    });
    builder.addCase(doCheckout.fulfilled, (state, action) => {
      state.orderResponse = action.payload.data.data;
      state.loading = false;
    });
    builder.addCase(doCheckout.rejected, (state, action) => {
      state.loading = false;
      state.checkoutError = action.payload as SerializedException;
    });
    builder.addCase(doPreviewOrder.pending, (state, action) => {
      state.previewLoading = true;
      state.previewRequestId = action.meta.requestId;
    });
    builder.addCase(doPreviewOrder.fulfilled, (state, action) => {
      if (state.previewRequestId !== action.meta.requestId) {
        return;
      }
      state.previewLoading = false;
      state.previewRequestId = undefined;
      state.orderPreviewResponse = action.payload.orderPreviewResponse;
      // Only clear the coupon error when this preview actually carried a coupon;
      // the automatic coupon-less fallback preview must keep the error visible.
      if (state.couponName) {
        state.couponError = null;
      }
    });
    builder.addCase(doPreviewOrder.rejected, (state, action) => {
      if (state.previewRequestId !== action.meta.requestId) {
        return;
      }
      const payload = action.payload as SerializedException;
      state.previewLoading = false;
      state.previewRequestId = undefined;
      if (action.meta.aborted) {
        return;
      }
      if (state.couponName) {
        state.couponError = payload?.message || 'Invalid coupon code. Please check and try again.';
        state.couponName = null;
      } else {
        state.errors.push(payload);
      }
    });
    builder.addCase(doFetchAllOrder.pending, state => {
      state.loading = true;
    });
    builder.addCase(doFetchAllOrder.fulfilled, (state, action) => {
      state.previousOrder = action.payload.data.filter(
        order => !pendingOrderStatus.includes(order.status),
      );
      state.pendingOrder = action.payload.data.filter(
        order => pendingOrderStatus.includes(order.status),
      );
      state.loading = false;
    });
    builder.addCase(doFetchAllOrder.rejected, (state, action) => {
      const payload = action.payload as SerializedException;
      state.loading = false;
      state.errors.push(payload);
    });
    builder.addCase(doCancelOrder.pending, state => {
      state.loading = true;
    });
    builder.addCase(doCancelOrder.fulfilled, state => {
      state.loading = false;
    });
    builder.addCase(doCancelOrder.rejected, (state, action) => {
      const payload = action.payload as SerializedException;
      state.loading = false;
      state.errors.push(payload);
    });
    builder.addCase(doCheckoutAws.pending, state => {
      state.loading = true;
      state.checkoutError = undefined;
      state.orderResponse = undefined;
    });
    builder.addCase(doCheckoutAws.fulfilled, (state, action) => {
      state.orderResponse = action.payload.data.data;
      state.loading = false;
    });
    builder.addCase(doCheckoutAws.rejected, (state, action) => {
      state.loading = false;
      state.checkoutError = action.payload as SerializedException;
    });
    builder.addCase(doFetchOrderById.pending, state => {
      state.loading = true;
    });
    builder.addCase(doFetchOrderById.fulfilled, (state, action) => {
      state.orderResponse = action.payload.order;
      state.loading = false;
    });
    builder.addCase(doFetchOrderById.rejected, (state, action) => {
      state.loading = false;
      const payload = action.payload as SerializedException;
      state.errors.push(payload);
    });
  },
});

const selectOrderFeature = (state: AppState) => state[ORDER_FEATURE_KEY];

export const selectAddOns = createSelector(
  selectOrderFeature,
  orderState => orderState.addOns,
);

export const selectTruePlatformPreviewRequest = createSelector(
  selectOrderFeature,
  orderState => orderState.truePlatformPreviewRequest,
);

export const selectLoading = createSelector(
  selectOrderFeature,
  orderState => orderState.loading,
);

export const selectErrors = createSelector(
  selectOrderFeature,
  orderState => orderState.errors,
);

export const selectPreviewLoading = createSelector(
  selectOrderFeature,
  orderState => orderState.previewLoading,
);

export const selectOrderPreviewRequest = createSelector(
  selectOrderFeature,
  orderState => orderState.orderPreviewRequest,
);

export const selectOrderPreviewResponse = createSelector(
  selectOrderFeature,
  orderState => orderState.orderPreviewResponse,
);

export const selectProductPreview = (productCode: string) => createSelector(
  selectOrderFeature,
  orderState => orderState.orderPreviewResponse
    ?.productsPreview.find(it => isSamePlanIgnoringEcom(it.productCode, productCode)),
);

export const selectSubTotalPrice = createSelector(
  selectOrderFeature,
  orderState => orderState.orderPreviewResponse
    ?.productsPreview.reduce((total, product) => total + product.subTotal, 0),
);

export const selectDiscountAmount = createSelector(
  selectOrderFeature,
  orderState => orderState.orderPreviewResponse
    ?.productsPreview.reduce((discount, product) => discount + product.discountAmount, 0),
);

export const selectPendingOrder = createSelector(
  selectOrderFeature,
  it => it.pendingOrder ?? [],
);

export const selectOrderResponse = createSelector(
  selectOrderFeature,
  it => it.orderResponse,
);

export const selectCheckoutError = createSelector(
  selectOrderFeature,
  it => it.checkoutError,
);

export const selectPreviewsTotal = createSelector(
  selectOrderFeature,
  it => it.orderPreviewResponse?.productsPreview.reduce((n, { total }) => n + total, 0) || 0,
);

export const selectPreviewsSubTotal = createSelector(
  selectOrderFeature,
  it => it.orderPreviewResponse?.productsPreview.reduce((n, { subTotal }) => n + subTotal, 0) || 0,
);

export const selectPreviewsTotalTax = createSelector(
  selectOrderFeature,
  it => it.orderPreviewResponse?.productsPreview.reduce((n, { tax }) => n + tax, 0) || 0,
);

export const selectDiscountTotal = createSelector(
  selectOrderFeature,
  it => it.orderPreviewResponse?.productsPreview.reduce(
    (n, { discountAmount }) => n + discountAmount,
    0,
  ) || 0,
);

export const selectProductPreviewByProductId = (productCode: string) => createSelector(
  selectOrderFeature,
  it => it.orderPreviewResponse?.productsPreview.find(
    it => isSamePlanIgnoringEcom(it.productCode, productCode),
  ),
);

export const selectProductPreviewByFeature = (feature: Feature) => createSelector(
  selectOrderFeature,
  it => it.orderPreviewResponse?.productsPreview.find(it => it.feature === feature),
);

export const selectOrderProductsPreview = createSelector(
  selectOrderFeature,
  it => it.orderPreviewResponse?.productsPreview || [],
);

export const selectCreditTotal = createSelector(
  selectOrderFeature,
  it => it.orderPreviewResponse?.productsPreview.reduce(
    (n, { creditAmount }) => n + creditAmount,
    0,
  ) || 0,
);

export const selectCouponName = createSelector(
  selectOrderFeature,
  it => it.couponName,
);

export const selectCouponError = createSelector(
  selectOrderFeature,
  it => it.couponError,
);

export const selectAppliedCoupon = createSelector(
  selectOrderFeature,
  it => it.orderPreviewResponse
    ?.productsPreview.find(it => it.appliedCoupon !== null)?.appliedCoupon,
);

export const selectAppliedPromotion = createSelector(
  selectOrderFeature,
  it => it.orderPreviewResponse
    ?.productsPreview.find(it => it.appliedPromotion !== null)?.appliedPromotion,
);

export const selectSaveCardAndBillingInfoBtnEnabled = createSelector(
  selectOrderFeature,
  it => it.saveCardAndBillingInfoBtnEnabled,
);

export const selectPreviousOrder = createSelector(
  selectOrderFeature,
  it => it.previousOrder ?? [],
);

export const selectPreviousOrderPreviewRequest = createSelector(
  selectOrderFeature,
  it => it.prevOrderPreviewRequest,
);

export const selectTermsAndConditionsAccepted = createSelector(
  selectOrderFeature,
  it => it.termsAndConditionsAccepted,
);

export const {
  setAddOns,
  setPrevTruePlatformPreviewRequest,
  setTruePlatformPreviewRequest,
  updateSaveCardAndBillingInfoBtnEnabled,
  updateProductPreviewRequest,
  updateTermsAndConditionsAccepted,
  resetOrderPreviewRequest,
  resetOrderPreviewResponse,
  resetOrderResponse,
  resetTermsAndConditionsAccepted,
  setCouponName,
  clearCoupon,
} = orderSlice.actions;

export default orderSlice.reducer;
